import { Box } from '@mui/material';
import { Languages, sleep } from '@remote-voice/utilities';
import { useEffect, useState } from 'react';

import ChatFooterMic from '@/components/organisms/chat/ChatFooterMic';
import ChatFooterMicDual, {
  MyMessagePosition,
} from '@/components/organisms/chat/ChatFooterMicDual';
import ChatFooterReactioners from '@/components/organisms/chat/ChatFooterReactioners';
import ChatFooterTemplate from '@/components/organisms/chat/ChatFooterTemplate';
import ChatFooterText from '@/components/organisms/chat/ChatFooterText';
import { ChatReaction } from '@/components/organisms/chat/ChatReactionList';
import { ChatRoomsTemplateMessage } from '@/types/graphql';

const ChatFooter = (props: {
  chatRoomId: string;
  sessionEntryCode: string;
  userId: string;
  userPassword: string;
  language: Languages;
  subUserLanguage: Languages;
  isGuestTyping: boolean;
  setIsGuestTyping: (value: boolean) => void;

  dualLangTranslation?: boolean;
  canGuideControl: boolean;
  myMessagePosition: MyMessagePosition;
  onChangeMyMessagePosition: (value: MyMessagePosition) => void;
  onOpenLanguageDialog: (value: boolean) => void;
  isMicMode: boolean; // マイクかテキストか
  onChangeMicMode: (value: boolean) => void;
  isTemplateMode: boolean;
  onChangeTemplateMode: (value: boolean) => void;
  isReactionMode: boolean;
  onChangeReactionMode: (value: boolean) => void;
  chatText: string;
  onChangeChatText: (text: string) => void;

  isRemoteMode: boolean;
  onChangeRemoteMode: () => void;
  onChangeRemoteModeDual: () => void;
  onSendMessage: (text: string, isSubUserSend?: boolean) => void;
  onSendTemplate: (templateId: string) => void;
  onSendImage: (isSubUserSend?: boolean) => void;
  onClickTemplateMessage?: () => void;

  canUseMic: boolean; // 利用可能なマイクがあるか
  isReadyMic: boolean; // 開始する準備が出来ているか
  isSimultaneous: boolean; // 同通
  isRecording: boolean;
  onMicStart: (isGuestRecording?: boolean) => void;
  onMicEnd: () => void;
  onMicCancel: () => void;

  ngWordRegExp?: RegExp;

  templateMessages: ChatRoomsTemplateMessage[];
  reactions?: ChatReaction[];
}) => {
  const [isWaitingEnd, setIsWaitingEnd] = useState(false);

  // キーボードに切り替えた際、音声入力の終了を待機するロジック
  const isReadyMic = props.isReadyMic;
  const onChangeMicMode = props.onChangeMicMode;
  useEffect(() => {
    if (isWaitingEnd && isReadyMic === true) {
      setIsWaitingEnd(false);
      onChangeMicMode(false);
      (async () => {
        await sleep(0);
        const el = document.getElementById('chat-input-text-area');
        el?.focus();
      })();
    }
  }, [isWaitingEnd, isReadyMic, onChangeMicMode]);

  return (
    <Box data-testid="chat-footer">
      {props.isMicMode ? (
        <>
          {props.isReactionMode && (
            // ChatFooterMicの角丸分だけ下げる
            <Box mb="-28px" pb="28px" bgcolor="white">
              <ChatFooterReactioners
                onCancel={() => props.onChangeReactionMode(false)}
                reactions={props.reactions ?? []}
              />
            </Box>
          )}
          {props.dualLangTranslation && props.canGuideControl ? (
            <ChatFooterMicDual
              canUseMic={props.canUseMic}
              isRemoteMode={props.isRemoteMode}
              onChangeRemoteModeDual={props.onChangeRemoteModeDual}
              isReadyMic={props.isReadyMic}
              isSimultaneous={props.isSimultaneous}
              isRecording={props.isRecording}
              onClickTemplateMessage={props.onClickTemplateMessage}
              isTemplateMode={props.isTemplateMode}
              onMicStart={props.onMicStart}
              onMicEnd={props.onMicEnd}
              subUserLanguage={props.subUserLanguage}
              onMicCancel={props.onMicCancel}
              isGuestTyping={props.isGuestTyping}
              isMicMode={props.isMicMode}
              myMessagePosition={props.myMessagePosition}
              onChangeMyMessagePosition={props.onChangeMyMessagePosition}
              onOpenLanguageDialog={props.onOpenLanguageDialog}
              onChangeToKeyboard={async (isGuestTyping?: boolean) => {
                props.setIsGuestTyping(!!isGuestTyping);
                if (props.isRecording) {
                  //マイク入力の終了を待機してから切り替える。
                  props.onMicEnd();
                  setIsWaitingEnd(true);
                } else {
                  props.onChangeMicMode(false);
                  await sleep(0);
                  const el = document.getElementById('chat-input-text-area');
                  el?.focus();
                }
              }}
            />
          ) : (
            <ChatFooterMic
              canUseMic={props.canUseMic}
              isReadyMic={props.isReadyMic}
              isSimultaneous={props.isSimultaneous}
              isRecording={props.isRecording}
              isRemoteMode={props.isRemoteMode}
              isTemplateMode={props.isTemplateMode}
              onSendImage={props.onSendImage}
              onClickTemplateMessage={props.onClickTemplateMessage}
              onChangeRemoteMode={props.onChangeRemoteMode}
              onMicStart={props.onMicStart}
              onMicEnd={props.onMicEnd}
              onMicCancel={props.onMicCancel}
              onChangeToKeyboard={async () => {
                if (props.isRecording) {
                  //マイク入力の終了を待機してから切り替える。
                  props.onMicEnd();
                  setIsWaitingEnd(true);
                } else {
                  props.onChangeMicMode(false);
                  await sleep(0);
                  const el = document.getElementById('chat-input-text-area');
                  el?.focus();
                }
              }}
            />
          )}
        </>
      ) : (
        <ChatFooterText
          text={props.chatText}
          onChangeText={props.onChangeChatText}
          onSendMessage={(text, isSubUserSend) => {
            props.onSendMessage(text, isSubUserSend);
          }}
          onSendImage={(isSubUserSend?: boolean) => {
            props.onSendImage(isSubUserSend);
          }}
          onChangeToMic={() => {
            props.onChangeChatText('');
            props.onChangeMicMode(true);
            if (props.isSimultaneous && props.isReadyMic) {
              props.onMicStart(props.isGuestTyping);
            }
          }}
          isGuestTyping={props.isGuestTyping}
          dualLangTranslation={props.dualLangTranslation}
          canGuideControl={props.canGuideControl}
        />
      )}
      {props.isTemplateMode && (
        <ChatFooterTemplate
          messages={props.templateMessages}
          language={props.language}
          isRemoteMode={props.isRemoteMode}
          onSend={props.onSendTemplate}
          onCancel={() => props.onChangeTemplateMode(false)}
        />
      )}
    </Box>
  );
};

export default ChatFooter;
